// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { slugify, useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import { ICONS } from '@Constants/iconsApp';
import { copyToClipboard } from '@Helpers/copy';
import client from '@Client';

import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/mode-jsx';
import 'ace-builds/src-noconflict/mode-xml';
import 'ace-builds/src-noconflict/snippets/xml';

import './SSOForm.scss';

export interface SSOInput extends FormSubmitOptions {
    type: string;
    displayName?: string;
    name?: string;
    idpMetadata?: string;
    idpMetadataUrl?: string;
    maximumAuthenticationLifetime?: string;

    metadataType?: string;
    callbackURL?: string;
}

export interface SSOValueFormOutput extends SSOInput {
    another?: boolean;
}

interface SSOFormProps {
    form: FormInstance;
    defaultValues?: SSOInput;
    isValid: boolean;
    onFinish: (values: SSOValueFormOutput) => void;
    onChange: () => void;
    onCancel?: () => void;
    forbiddenNames?: string[];
}

export const SSOForm = ({
    form,
    defaultValues,
    isValid,
    forbiddenNames,
    onFinish,
    onChange,
    onCancel,
}: SSOFormProps): ReactElement => {
    const i = useLocalMessage();
    const defaultType = useMemo(
        () => (defaultValues?.idpMetadataUrl ? 'idpMetadataUrl' : defaultValues?.idpMetadata ? 'idpMetadata' : ''),
        [defaultValues],
    );
    const [formType, setFormType] = useState<string>(defaultType);

    const baseUrl = window.location.origin + client.apiUrl;

    const [callbackURL, setCallbackURL] = useState<string | undefined>(
        defaultValues?.displayName && `${baseUrl}/sso/${defaultValues?.displayName}`,
    );

    const onFinishHandler = useCallback(
        (values: SSOInput) => {
            if (values.metadataType === 'idpMetadataUrl') {
                delete values.idpMetadata;
            }

            if (values.metadataType === 'idpMetadata') {
                delete values.idpMetadataUrl;
            }
            delete values.metadataType;

            if (values.displayName && values.displayName.trim() !== '') {
                values.name = slugify(values.displayName);
            }
            delete values.callbackURL;

            onFinish(values);
        },
        [onFinish],
    );

    useEffect(() => {
        form.setFieldsValue({ callbackURL });
    }, [callbackURL, form]);

    return (
        <THForm<SSOInput>
            name="task-form"
            className="sso-form"
            onFinish={onFinishHandler}
            initialValues={{
                type: 'saml',
                ...defaultValues,
                callbackURL,
                maximumAuthenticationLifetime: '1 hours',
                metadataType: defaultType,
            }}
            form={form}
            onValuesChange={onChange}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} formName="task-form" />}>
                <THForm.Item name="type" label={i('configPlateform.sso.type')}>
                    <THInput disabled={true} />
                </THForm.Item>

                <THForm.Item
                    name="displayName"
                    label={i('configPlateform.sso.name')}
                    rules={[
                        { required: !defaultValues?.displayName, message: i('configPlateform.required.general') },
                        {
                            validator: (_, value) => {
                                const nameTreated = slugify(value);

                                if (defaultValues?.displayName === value) {
                                    return Promise.resolve(i('common.already-exist'));
                                }

                                if (forbiddenNames?.includes(nameTreated)) {
                                    return Promise.reject(i('common.already-exist'));
                                }

                                if (/[^\w\-_]/.test(value)) {
                                    return Promise.reject(i('common.urlSuffix'));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <THInput
                        disabled={!!defaultValues?.displayName}
                        onChange={({ target }) => {
                            setCallbackURL(`${baseUrl}/sso/${target.value}`);
                        }}
                    />
                </THForm.Item>

                <THForm.Item
                    name="metadataType"
                    rules={[{ required: true }]}
                    label={i('configPlateform.sso.idpMetadata-type')}
                >
                    <THSelect
                        placeholder="Choose type"
                        onChange={(value: string) => setFormType(value)}
                        options={[
                            {
                                label: 'xml',
                                value: 'idpMetadata',
                            },
                            { label: 'url', value: 'idpMetadataUrl' },
                        ]}
                    />
                </THForm.Item>

                <THForm.Item
                    name="idpMetadata"
                    label={i('configPlateform.sso.idpMetadata-value')}
                    rules={[{ required: formType === 'idpMetadata' }]}
                    hidden={formType !== 'idpMetadata'}
                >
                    <AceEditor
                        width="100%"
                        height="15rem"
                        mode={'xml'}
                        theme="tomorrow"
                        setOptions={{ useWorker: false }}
                    />
                </THForm.Item>

                <THForm.Item
                    name="idpMetadataUrl"
                    label={i('configPlateform.sso.idpMetadata-value')}
                    rules={[{ required: formType === 'idpMetadataUrl', type: 'url' }]}
                    hidden={formType !== 'idpMetadataUrl'}
                >
                    <THInput />
                </THForm.Item>

                <THForm.Item
                    name="maximumAuthenticationLifetime"
                    label={i('configPlateform.sso.maximumAuthenticationLifetime')}
                >
                    <THNumberSelect options={['hours', 'days']} min={1} />
                </THForm.Item>

                {callbackURL && (
                    <THForm.Item name="callbackURL" label={i('configPlateform.sso.callbackURL')}>
                        <THInput
                            className="sso-form__callback-url"
                            disabled
                            addonAfter={
                                <ICONS.CloneIcon
                                    role="button"
                                    onClick={() => copyToClipboard(callbackURL, i('notification.copy'))}
                                />
                            }
                        />
                    </THForm.Item>
                )}
            </THForm.Layout>
        </THForm>
    );
};
